import type { EventTime } from "@/types/events";

export const permissionArray: string[] = [
  "add user",
  "get user",
  "get user list",
  "update user",
  "delete user",
  "add role",
  "get role",
  "get role list",
  "update role",
  "delete role",
  "delete permission",
  "add permission",
  "get permission",
  "get permission list",
  "get role permission list",
  "add role permission list",
  "get role permission",
  "update role permission",
  "delete role permission",
  "get category",
  "delete category",
  "get category list",
  "add category list",
  "update category",
  "update product",
  "add product",
  "get product",
  "get product list",
  "delete product",
  "delete product image",
  "get product image",
  "get product image list",
  "add product image",
  "update product image",
  "create single sub-category",
  "fetch single sub-category",
  "update single sub-category",
  "get sub-category list",
  "delete sub-category",
  "create single vendor",
  "delete single vendor",
  "get single vendor",
  "get vendor list",
  "update vendor list",
  "get dashboard analytics",
  "get top product",
  "get near expiry product",
  "get sales report",
  "update campaign status",
  "update vendor status",
  "get order list",
  "update order status",
  "update order payment status",
  "update order delivery status",
  "get delivery status of order",
];

export const saveToken = (token: string) => {
  localStorage.setItem("access_token", token);
};

export const saveRefreshToken = (token: string) => {
  localStorage.setItem("refresh_token", token);
};

export const saveCurrentUser = (user: Record<string, any>) => {
  const existingUser = getCurrentUser() || {};
  const shouldReplaceExistingUser =
    Boolean(user.isAdmin) ||
    user.role === "ADMIN" ||
    (Boolean(user.email) &&
      Boolean(existingUser.email) &&
      existingUser.email !== user.email);

  if (user.isAdmin || user.role === "ADMIN") {
    localStorage.removeItem("fullName");
  }

  localStorage.setItem(
    "user",
    JSON.stringify(
      shouldReplaceExistingUser ? user : { ...existingUser, ...user },
    ),
  );
};

export const getToken = (): string | null =>
  localStorage.getItem("access_token");

export const getRefreshToken = (): string | null =>
  localStorage.getItem("refresh_token");

interface UserData {
  email?: string;
  userId?: string;
  userName?: string;
  Permissions?: string[];
  [key: string]: any;
}
export const getCurrentUser = (key?: keyof UserData) => {
  const data = JSON.parse(localStorage.getItem("user") || "{}");
  if (!key) return data;
  return data[key];
};

export const getFullName = (): string => {
  return JSON.parse(localStorage.getItem("fullName") || "null");
};

export const clearOnLogout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  localStorage.removeItem("fullName");
  // Clear signup wizard persistence so a fresh session always starts clean.
  // Without this, stale step keys and draft data survive across logouts and
  // cause step skipping or incorrect wizard redirects on the next login.
  localStorage.removeItem("bfc_sign_up_journey_draft");
  for (const key of [...Object.keys(localStorage)]) {
    if (key.startsWith("signup_step_") || key.startsWith("restore_signup_step_")) {
      localStorage.removeItem(key);
    }
  }
};

export const combineClasses = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

export const env = (key: keyof ImportMetaEnv, defaultValue = ""): string => {
  const value = import.meta.env[key];
  return typeof value === "string" ? value : defaultValue;
};

export const requiredMessage = (name: string) => `${name} is required`;
export const maxLengthMessage = (name: string, max = 255) =>
  `${name} cannot exceed ${max} characters`;

export const onlyNumbersInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];

  if (
    allowedKeys.includes(e.key) ||
    (e.ctrlKey && ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())) ||
    (e.metaKey && ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase()))
  ) {
    return;
  }

  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
  }
};

export const capitalizeText = (text?: string) => {
  if (!text) return "";
  return text
    .split(" ")
    .map((word) => {
      const [first, ...rest] = word;
      return first ? first.toUpperCase() + rest.join("") : "";
    })
    .join(" ");
};

export const CURRENCY_SYMBOL = "₹";

export const getInitialValues = <T extends Record<string, any>>(config: T) =>
  Object.fromEntries(
    Object.entries(config).map(([key, val]) => [key, val.defaultValue]),
  );

export const getSelectOptions = (arr?: { label: any; value: any }[]) =>
  (arr ?? []).map((item) => ({
    label: String(item.label),
    value: String(item.value),
  }));

export const createEnum = (obj: Record<string, any>) => {
  Object.freeze(obj);
};

export const dateConverter = (str: string | Date) => {
  const d = new Date(str);
  let mm = d.getMonth() + 1;
  let dd = d.getDate();

  return [
    (dd > 9 ? "" : "0") + dd,
    (mm > 9 ? "" : "0") + mm,
    d.getFullYear(),
  ].join("-");
};

export const inputDateFormat = (date?: string | Date) =>
  date ? new Date(date).toISOString().split("T")[0] : "";
export const formatDateForApi = (date: Date) => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getDaysFilterRange = (days: number) => {
  const toDate = new Date();
  const fromDate = new Date();

  fromDate.setDate(toDate.getDate() - days);

  fromDate.setUTCHours(0, 0, 0, 0);
  toDate.setUTCHours(23, 59, 59, 0);

  const format = (date: any) => date.toISOString().split(".")[0] + "Z";

  return {
    fromDate: format(fromDate),
    toDate: format(toDate),
  };
};

export const updateParams = (
  params: Record<string, any>,
  setParams: React.Dispatch<React.SetStateAction<Map<string, any>>>,
) => {
  setParams((prev) => {
    Object.entries(params || {}).forEach(([key, value]) => {
      prev.set(key, value);
    });
    return prev;
  });
};

export const addBaseUrl = (url?: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const baseUrl = env("VITE_API_BASE_URL").replace(/\/$/, "");
  const cleanUrl = url.startsWith("/") ? url : `/${url}`;
  return `${baseUrl}/api/files${cleanUrl}`;
};

export const checkPermission = (permission?: string) => {
  const permissions = getCurrentUser("permissions") || [];
  return permissions.includes(permission);
};

export const conditionalColumns = <T>(
  condition: boolean,
  columns: T[] | T,
): T[] => {
  const colArray = Array.isArray(columns) ? columns : [columns];
  return condition ? colArray : [];
};

export const formatNumberWithSuffix = (value: number, roundDigits = 1) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(roundDigits)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(roundDigits)}k`;
  return value.toString();
};

export const convertTimeStringToEventTime = (time: string): EventTime => {
  const [hourStr, minuteStr] = time.split(":");

  return {
    hour: Number(hourStr),
    minute: Number(minuteStr),
    second: 0,
    nano: 0,
  };
};

export const mapStartEndTime = (startTime: string, endTime: string) => {
  return {
    eventStartTime: convertTimeStringToEventTime(startTime),
    eventEndTime: convertTimeStringToEventTime(endTime),
  };
};

export function formatForDateTimeLocal(dateString: string) {
  return dateString.replace("T", "").slice(0, 16);
}

export const formatSubmittedAt = (
  isoDate: string,
  includeTime: boolean = false,
): string => {
  if (!isoDate) return "";

  const date = new Date(isoDate);

  const day = date.getDate();
  const year = date.getFullYear();

  const month = date.toLocaleString("en-GB", {
    month: "long",
  });

  const formattedDate = `${day} ${month} ${year}`;

  if (!includeTime) {
    return formattedDate;
  }

  const formattedTime = date.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} | ${formattedTime}`;
};

export const formatTimeOnly = (isoDate: string): string => {
  if (!isoDate) return "";

  const date = new Date(isoDate);

  return date
    .toLocaleString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    })
    ?.toUpperCase();
};

export const formatRatingValue = (
  rating: number | string | null | undefined,
): string => {
  if (rating === null || rating === undefined || rating === "") return "-";

  const numericRating =
    typeof rating === "number" ? rating : Number.parseFloat(String(rating));

  if (Number.isNaN(numericRating)) return "-";

  return Number.isInteger(numericRating)
    ? numericRating.toFixed(1)
    : String(numericRating);
};

export function generateId(): string {
  if (
    typeof globalThis !== "undefined" &&
    globalThis.crypto &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  // Fallback
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export function formatPermissionName(str: string) {
  if (!str) return "";

  return (
    str
      // ✅ insert space before every uppercase letter (camelCase → "camel Case")
      .replace(/([A-Z])/g, " $1")
      // ✅ replace underscores with spaces (SNAKE_CASE → "SNAKE CASE")
      .replace(/_/g, " ")
      // ✅ lowercase everything
      .toLowerCase()
      // ✅ capitalize first letter
      .replace(/^./, (char) => char.toUpperCase())
      // ✅ remove any double spaces from combined replacements
      .replace(/\s+/g, " ")
      .trim()
  );
}

export const captalizeEachWord = (str?: string) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const humanizeEnumValue = (value: string) =>
  captalizeEachWord(value.trim().toLowerCase().replace(/_/g, " "));

export const getIndustryLabels = (
  industries?: Array<{ id: number; categoryName: string } | string> | null,
): string[] =>
  (industries ?? []).map((item) =>
    typeof item === "string" ? humanizeEnumValue(item) : item.categoryName,
  );

export const formatListSummary = (items: string[], maxVisible = 2) => {
  if (items.length === 0) return "";
  if (items.length <= maxVisible) return items.join(", ");
  return `${items.slice(0, maxVisible).join(", ")}, ...`;
};

export const firstCapital = (str?: string) => {
  if (!str) return "";
  return str[0]?.toUpperCase() + str.slice(1).toLocaleLowerCase();
};
